import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, PieChart as PieChartIcon, Plus, Trash2, X } from 'lucide-react';
import { ASSET_CLASS_BY_ID, ASSET_CLASSES_CATALOG, type AssetClassDefinition } from '@/constants/asset-classes';
import { Layout } from '@/components/Layout';
import { useI18n } from '@/i18n/use-i18n';
import { assetClassesService, type AssetClassRecord } from '@/services';
import { cn } from '@/utils/cn';
import './asset-classes.css';
import { AssetClassCode } from '@/types/assets';

type GroupId = AssetClassCode;
type GroupSelection = AssetClassRecord;

const clampPercentage = (value: number) => {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

export function AssetClasses() {
  const { strings } = useI18n();
  const queryClient = useQueryClient();
  const [selectedGroups, setSelectedGroups] = useState<GroupSelection[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const assetClassCopy = strings.assetClassesCatalog;

  const assetClassesQueryKey = ['asset-classes'];

  const {
    data: fetchedGroups,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: assetClassesQueryKey,
    queryFn: assetClassesService.list,
  });

  const catalog = useMemo(
    () =>
      ASSET_CLASSES_CATALOG.map((group) => {
        const copy = assetClassCopy[group.id];
        return {
          ...group,
          name: copy?.name ?? group.name,
          description: copy?.description ?? group.description,
        };
      }),
    [assetClassCopy],
  );

  useEffect(() => {
    if (fetchedGroups) {
      setSelectedGroups(fetchedGroups);
    }
  }, [fetchedGroups]);

  const createMutation = useMutation({
    mutationFn: assetClassesService.create,
    onSuccess: (record) => {
      queryClient.setQueryData<GroupSelection[] | undefined>(assetClassesQueryKey, (prev) =>
        prev ? [...prev, record] : [record],
      );
      setSelectedGroups((prev) => [...prev, record]);
      setIsDialogOpen(false);
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.assetClasses.errors.add,
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: assetClassesService.update,
    onSuccess: (record) => {
      queryClient.setQueryData<GroupSelection[] | undefined>(assetClassesQueryKey, (prev) =>
        !prev
          ? [record]
          : prev.some((item) => item.id === record.id)
            ? prev.map((item) => (item.id === record.id ? record : item))
            : [...prev, record],
      );
      setSelectedGroups((prev) =>
        prev.some((item) => item.id === record.id)
          ? prev.map((item) => (item.id === record.id ? record : item))
          : [...prev, record],
      );
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.assetClasses.errors.update,
      );
    },
  });

  const removeMutation = useMutation({
    mutationFn: assetClassesService.remove,
    onSuccess: (_, id) => {
      queryClient.setQueryData<GroupSelection[] | undefined>(assetClassesQueryKey, (prev) =>
        (prev ?? []).filter((item) => item.id !== id),
      );
      setSelectedGroups((prev) => prev.filter((item) => item.id !== id));
    },
    onError: (mutationError) => {
      setActionError(
        mutationError instanceof Error ? mutationError.message : strings.assetClasses.errors.remove,
      );
    },
  });

  const selectedIds = useMemo(() => new Set(selectedGroups.map((group) => group.code)), [selectedGroups]);

  const total = useMemo(
    () => selectedGroups.reduce((sum, group) => sum + group.percentage, 0),
    [selectedGroups],
  );

  const normalizedGroups = useMemo(() => {
    const groups = selectedGroups
      .map((group) => {
        const definition = ASSET_CLASS_BY_ID[group.code];
        if (!definition) return null;
        const copy = assetClassCopy[group.code];
        return {
          ...definition,
          name: copy?.name ?? definition.name,
          description: copy?.description ?? definition.description,
          percentage: group.percentage,
          recordId: group.id,
          code: group.code,
        };
      })
      .filter(Boolean) as Array<AssetClassDefinition & { percentage: number; recordId: string; code: GroupId }>;

    return groups;
  }, [assetClassCopy, selectedGroups]);

  const applyLocalPercentage = (recordId: string, value: number) => {
    let applied = 0;
    setSelectedGroups((prev) => {
      const sanitized = clampPercentage(value);
      const otherTotal = prev.reduce(
        (sum, group) => (group.id === recordId ? sum : sum + group.percentage),
        0,
      );
      const maxAllowed = Math.max(0, 100 - otherTotal);
      applied = Math.min(sanitized, maxAllowed);

      return prev.map((group) =>
        group.id === recordId ? { ...group, percentage: applied } : group,
      );
    });
    return applied;
  };

  const handlePercentageChange = (recordId: string, value: number) => {
    setActionError('');
    applyLocalPercentage(recordId, value);
  };

  const handlePercentageCommit = (recordId: string, value: number) => {
    setActionError('');
    const applied = applyLocalPercentage(recordId, value);
    updateMutation.mutate({ id: recordId, percentage: applied });
  };

  const handleRemoveGroup = (recordId: string) => {
    setSelectedGroups((prev) => prev.filter((group) => group.id !== recordId));
    setActionError('');
    removeMutation.mutate(recordId);
  };

  const handleAddGroup = (code: GroupId) => {
    if (selectedIds.has(code)) return;
    setActionError('');
    createMutation.mutate({ code, percentage: 0 });
  };

  const isBusy =
    isLoading || createMutation.isPending || updateMutation.isPending || removeMutation.isPending || isFetching;
  const queryErrorMessage = isError
    ? error instanceof Error
      ? error.message
      : strings.assetClasses.errors.load
    : '';

  return (
    <Layout>
      <Tooltip.Provider delayDuration={80}>
        <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <section className="asset-groups app-page">
            <header className="asset-groups__header">
              <div>
                <p className="asset-groups__eyebrow">Carteira meta</p>
                <h1>{strings.assetClasses.title}</h1>
                <span>{strings.assetClasses.subtitle}</span>
              </div>
            </header>

            {queryErrorMessage ? (
              <div className="asset-groups__feedback asset-groups__feedback--error">{queryErrorMessage}</div>
            ) : null}

            {actionError ? (
              <div className="asset-groups__feedback asset-groups__feedback--error">{actionError}</div>
            ) : null}

            <SummaryBar total={total} />

            <div className="asset-groups__panel">
              {isLoading ? (
                <div className="asset-groups__state app-state app-state--loading">
                  {strings.assetClasses.loading}
                </div>
              ) : normalizedGroups.length === 0 ? (
                <div className="asset-groups__empty-state">
                  <span className="asset-groups__empty-icon"><PieChartIcon size={28} /></span>
                  <strong>Nenhuma classe configurada</strong>
                  <p>Adicione classes de ativos para definir como distribuir seus aportes.</p>
                  <Dialog.Trigger asChild>
                    <button
                      type="button"
                      className="asset-groups__empty-cta"
                      disabled={isBusy}
                    >
                      <Plus size={15} />
                      {strings.assetClasses.add}
                    </button>
                  </Dialog.Trigger>
                </div>
              ) : (
                <div className="asset-groups__cards">
                  {normalizedGroups.map((group) => (
                    <GroupCard
                      key={group.recordId}
                      group={group}
                      onChange={handlePercentageChange}
                      onCommit={handlePercentageCommit}
                      onRemove={handleRemoveGroup}
                      disabled={isBusy}
                    />
                  ))}

                  <Dialog.Trigger asChild>
                    <button
                      type="button"
                      className="asset-groups__add-card"
                      aria-label={strings.assetClasses.add}
                      disabled={isBusy}
                    >
                      <span>{strings.assetClasses.add}</span>
                    </button>
                  </Dialog.Trigger>
                </div>
              )}
            </div>

            <PreviewCard groups={normalizedGroups} total={total} />
          </section>

          <Dialog.Portal>
            <Dialog.Overlay className="asset-groups__dialog-overlay" />
            <Dialog.Content
              className="asset-groups__dialog"
              aria-label={strings.assetClasses.add}
            >
              <div className="asset-groups__dialog-header">
                <div>
                  <Dialog.Title>{strings.assetClasses.dialog.title}</Dialog.Title>
                  <Dialog.Description>
                    {strings.assetClasses.dialog.description}
                  </Dialog.Description>
                </div>
                <Dialog.Close
                  className="asset-groups__dialog-close"
                  aria-label={strings.assetClasses.dialog.close}
                >
                  <X size={18} />
                </Dialog.Close>
              </div>

              <div className="asset-groups__dialog-list" role="list">
                {catalog.map((group) => {
                  const Icon = group.icon;
                  const isDisabled = selectedIds.has(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      className={cn(
                        'asset-groups__dialog-item',
                        isDisabled && 'asset-groups__dialog-item--disabled',
                      )}
                      onClick={() => handleAddGroup(group.id)}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                    >
                      <div
                        className="asset-groups__dialog-avatar"
                        style={{ '--chip-color': group.color } as CSSProperties}
                      >
                        <Icon size={18} />
                      </div>
                      <div className="asset-groups__dialog-info">
                        <div className="asset-groups__dialog-title">
                          <span>{group.name}</span>
                          <small>{group.code}</small>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <span
                                className="asset-groups__tooltip-trigger"
                                aria-label={strings.assetClasses.dialog.tooltipLabel}
                              >
                                <Info size={14} />
                              </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content className="asset-groups__tooltip">
                              {group.description}
                              <Tooltip.Arrow className="asset-groups__tooltip-arrow" />
                            </Tooltip.Content>
                          </Tooltip.Root>
                        </div>
                        <p>{group.description}</p>
                      </div>
                      {isDisabled && (
                        <span className="asset-groups__pill">
                          {strings.assetClasses.dialog.added}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </Tooltip.Provider>
    </Layout>
  );
}

interface GroupCardProps {
  group: AssetClassDefinition & { percentage: number; recordId: string; code: GroupId };
  onChange: (recordId: string, value: number) => void;
  onCommit: (recordId: string, value: number) => void;
  onRemove: (recordId: string) => void;
  disabled?: boolean;
}

function GroupCard({ group, onChange, onCommit, onRemove, disabled }: GroupCardProps) {
  const { strings } = useI18n();
  const Icon = group.icon;
  const sliderBackground = `linear-gradient(90deg, ${group.color} 0%, ${group.color} ${group.percentage}%, var(--border) ${group.percentage}%, var(--border) 100%)`;
  const sliderId = `${group.recordId}-slider`;

  return (
    <article className="asset-group-card" role="group" aria-label={group.name}>
      <div className="asset-group-card__top">
        <div className="asset-group-card__title">
          <div
            className="asset-group-card__icon"
            style={{ '--chip-color': group.color } as CSSProperties}
            aria-hidden="true"
          >
            <Icon size={18} strokeWidth={2.2} />
          </div>
          <span>{group.name}</span>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span
                className="asset-groups__tooltip-trigger"
                aria-label={strings.assetClasses.dialog.tooltipLabel}
              >
                <Info size={14} />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content className="asset-groups__tooltip">
              {group.description}
              <Tooltip.Arrow className="asset-groups__tooltip-arrow" />
            </Tooltip.Content>
          </Tooltip.Root>
        </div>
        <button
          type="button"
          onClick={() => onRemove(group.recordId)}
          className="asset-group-card__remove"
          aria-label={`${strings.assetClasses.remove} ${group.name}`}
          disabled={disabled}
        >
          <Trash2 size={15} />
        </button>
      </div>

      <div className="asset-group-card__controls">
        <input
          id={sliderId}
          type="range"
          min={0}
          max={100}
          step={1}
          value={group.percentage}
          onChange={(event) => onChange(group.recordId, Number(event.target.value))}
          onPointerUp={(event) => onCommit(group.recordId, Number(event.currentTarget.value))}
          className="asset-group-card__slider"
          disabled={disabled}
          style={
            {
              backgroundImage: sliderBackground,
              '--thumb-color': group.color,
            } as CSSProperties
          }
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={group.percentage}
          aria-label={`${strings.assetClasses.sliderAriaPrefix} ${group.name}`}
        />
        <div className="asset-group-card__inputs">
          <div className="asset-group-card__input">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={group.percentage}
              onChange={(event) => onChange(group.recordId, Number(event.target.value))}
              onBlur={(event) => onCommit(group.recordId, Number(event.target.value))}
              disabled={disabled}
              aria-label={`${strings.assetClasses.valueAriaPrefix} ${group.name}`}
            />
            <span>%</span>
          </div>
          <span className="asset-group-card__hint">
            {group.percentage}
            {strings.assetClasses.percentageOfTotal}
          </span>
        </div>
      </div>
    </article>
  );
}

interface PreviewCardProps {
  groups: Array<AssetClassDefinition & { percentage: number }>;
  total: number;
}

function PreviewCard({ groups, total }: PreviewCardProps) {
  const { strings } = useI18n();
  const donutSize = 260;
  const donutRadius = 70;
  const donutStrokeWidth = 18;

  const donutData = useMemo(
    () => {
      const data: DonutDatum[] = groups
        .filter((group) => group.percentage > 0)
        .map((group) => ({
          label: group.name,
          value: group.percentage,
          color: group.color,
        }));

      const remaining = Math.max(0, 100 - total);
      if (remaining > 0) {
        data.push({
          label: strings.assetClasses.preview.remaining,
          value: remaining,
          color: 'var(--border)',
        });
      }

      return data;
    },
    [groups, total],
  );

  return (
    <section
      className="asset-groups__preview"
      aria-label={strings.assetClasses.preview.ariaLabel}
    >
      <header className="asset-groups__preview-head">
        <h3>{strings.assetClasses.preview.title}</h3>
        <p>Distribuição visual da sua carteira meta</p>
      </header>
      <div className="asset-groups__preview-body">
        <div className="asset-groups__legend-block">
          {groups.map((group) => (
            <div className="asset-groups__legend-item" key={group.id}>
              <span
                className="asset-groups__legend-dot"
                style={{ '--chip-color': group.color } as CSSProperties}
                aria-hidden="true"
              />
              <span>{group.name}</span>
            </div>
          ))}
        </div>

        <div className="asset-groups__donut" aria-hidden="true">
          <DonutChart
            data={donutData}
            size={donutSize}
            radius={donutRadius}
            strokeWidth={donutStrokeWidth}
          />
          <div className="asset-groups__donut-center">
            <strong>{total}%</strong>
          </div>
        </div>

        <div className="asset-groups__legend-block asset-groups__legend-block--values">
          {groups.map((group) => (
            <div className="asset-groups__legend-item asset-groups__legend-item--value" key={group.id}>
              <strong>{group.percentage}%</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

type DonutDatum = {
  label: string;
  value: number;
  color: string;
};

interface DonutChartProps {
  data: DonutDatum[];
  size?: number;
  radius?: number;
  strokeWidth?: number;
}

function DonutChart({ data, size = 200, radius = 70, strokeWidth = 18 }: DonutChartProps) {
  const outerRadius = radius + strokeWidth / 2;
  const innerRadius = Math.max(radius - strokeWidth / 2, 0);

  const chartData = data.length > 0 ? data : [{ label: 'empty', value: 1, color: 'var(--bg-muted)' }];

  return (
    <PieChart width={size} height={size}>
      <Pie
        data={chartData}
        cx={size / 2}
        cy={size / 2}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={90}
        endAngle={-270}
        dataKey="value"
        stroke="none"
      >
        {chartData.map((entry, index) => (
          <Cell key={`${entry.label}-${index}`} fill={entry.color} />
        ))}
      </Pie>
    </PieChart>
  );
}

function SummaryBar({ total }: { total: number }) {
  const { strings } = useI18n();
  const isBalanced = total === 100;
  const width = Math.min(total, 100);

  return (
    <div
      className="asset-groups__summary"
      role="region"
      aria-label={strings.assetClasses.summary.label}
    >
      <div className="asset-groups__summary-row">
        <span>{strings.assetClasses.summary.label}</span>
        <strong className={cn('asset-groups__summary-total', isBalanced ? 'is-success' : 'is-warning')}>
          {total}%
        </strong>
      </div>
      <div className="asset-groups__summary-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={total}>
        <div
          className="asset-groups__summary-fill"
          style={{ width: `${width}%`, backgroundColor: isBalanced ? 'var(--color-success)' : 'var(--color-warning)' }}
        />
      </div>
      {!isBalanced && (
        <p className="asset-groups__summary-hint">{strings.assetClasses.summary.hint}</p>
      )}
    </div>
  );
}


